<template>
    <v-dialog :value="value" max-width="480px" persistent @keydown.esc="close" aria-label="Confirm credit change">
        <v-card id="creditChangeConfirmDialog">
            <v-card-title>
                <span class="mr-2" style="text-transform: uppercase; font-weight: 100; font-size: 16px">{{ title }}</span>
                <v-btn @click="close" :disabled="loading" right top absolute icon color="primary" aria-label="Close dialog"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-subtitle>
                <v-chip class="mt-1" small outlined color="primary"> {{ project.title }}</v-chip>
            </v-card-subtitle>
            <v-card-text class="mt-2">
                <ul v-if="changes.length" class="mb-3 pl-4">
                    <li v-for="(change, i) in changes" :key="i">{{ change }}</li>
                </ul>

                <div class="qgreycard my-0">
                    <div class="d-flex"><span>Current usage</span><span class="ml-auto">{{ format(before) }}</span></div>
                    <div class="d-flex"><span>New usage</span><span class="ml-auto font-weight-bold">{{ format(after) }}</span></div>
                    <v-divider class="my-1" />
                    <div class="d-flex">
                        <span>Change per month</span>
                        <span class="ml-auto font-weight-bold">{{ delta > 0 ? "+" : "" }}{{ format(delta) }} {{ unit }}</span>
                    </div>
                </div>

                <!-- Was jetzt gebucht wird, je nach Zustand des Projekts -->
                <div class="mt-3 hint">
                    <template v-if="project.demo_project">This project does not use credits.</template>
                    <template v-else-if="project.paused">
                        <v-icon x-small class="mr-1">mdi-pause-circle-outline</v-icon>
                        This project is paused. Nothing is charged now - {{ format(after) }} {{ unit }} per month are reserved when you resume it.
                    </template>
                    <template v-else-if="split.reserveNow > 0 || split.releaseLater > 0">
                        <span v-if="split.reserveNow > 0"><b>{{ format(split.reserveNow) }} {{ unit }}</b> are reserved now.</span>
                        <span v-else>Nothing is charged now.</span>
                        <span v-if="split.releaseLater > 0">
                            After the current update cycle<span v-if="releaseDate"> ({{ formatDate(releaseDate) }})</span> {{ format(split.releaseLater) }} {{ unit }} are
                            released, from then on the project uses {{ format(after) }} per month.
                        </span>
                        <span v-if="creditState && split.reserveNow > 0">
                            Remaining afterwards: <b>{{ format(creditState.remaining - split.reserveNow) }}</b> of {{ format(creditState.limit) }}.
                        </span>
                    </template>
                    <template v-else>The credit usage does not change.</template>
                </div>

                <div v-if="creditStateLoading" class="mt-2 hint grey--text">Loading your credits...</div>
                <div v-else-if="creditStateFailed && split.reserveNow > 0 && reserves" class="mt-2 hint grey--text">
                    Your remaining credits could not be loaded. They are checked again when you confirm.
                </div>

                <div class="d-flex">
                    <v-chip v-if="insufficient" class="my-2 mx-auto py-1" style="white-space: normal; height: auto" color="error">
                        <v-icon class="mr-1">mdi-alert</v-icon>
                        Not enough credits: {{ format(split.reserveNow) }} {{ unit }} required, {{ format(creditState.remaining) }} remaining.
                    </v-chip>
                </div>
            </v-card-text>
            <v-card-actions class="px-auto">
                <v-btn class="mr-3" text rounded :disabled="loading" @click="close"> Cancel </v-btn>
                <v-btn :loading="loading" :disabled="insufficient || creditStateLoading" rounded class="ml-auto" color="primary" @click="$emit('confirm')"> confirm </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import { fetchCreditState, holdsReservation, splitCreditChange } from "../helper/projectStatus"

    // Bestätigung vor jeder Änderung, die die Credit-Reservierung eines Projekts verändert
    // (Intervall, Plattformen). Zeigt Verbrauch vorher/nachher, was sofort reserviert wird und das
    // Restkontingent danach; reicht es nicht, ist "confirm" gesperrt. Die API prüft beim Speichern
    // trotzdem noch einmal atomar - der Dialog ist die Vorab-Anzeige, nicht die Absicherung.
    //   props  before/after: Verbrauch pro Monat in Credits, unit: "keyword updates" | "prompt updates"
    //          afterInterval: bisherige Plattformen mit neuem Intervall (nur nötig, wenn zugleich Plattformen
    //          dazukommen) - daraus folgt, was sofort reserviert und was erst zum Zyklusende frei wird
    //   events confirm, input(false)
    export default {
        name: "CreditChangeConfirmDialog",
        props: {
            value: { type: Boolean, default: false },
            project: { type: Object, required: true },
            title: { type: String, default: "Confirm change" },
            changes: { type: Array, default: () => [] },
            before: { type: Number, default: 0 },
            after: { type: Number, default: 0 },
            unit: { type: String, default: "keyword updates" },
            releaseDate: { type: Date, default: null },
            afterInterval: { type: Number, default: null },
            loading: { type: Boolean, default: false }
        },
        data() {
            return { creditState: null, creditStateLoading: false, creditStateFailed: false }
        },
        computed: {
            delta() {
                return this.after - this.before
            },
            reserves() {
                return holdsReservation(this.project)
            },
            // ohne afterInterval: reine Intervalländerung, der gesamte Unterschied ist Intervall-Anteil
            split() {
                return splitCreditChange({ before: this.before, afterInterval: this.afterInterval === null ? this.after : this.afterInterval, after: this.after })
            },
            insufficient() {
                return this.reserves && this.split.reserveNow > 0 && !!this.creditState && this.split.reserveNow > this.creditState.remaining
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(val) {
                    if (val) this.loadCreditState()
                }
            }
        },
        methods: {
            async loadCreditState() {
                this.creditState = null
                this.creditStateFailed = false
                if (!this.reserves) return
                this.creditStateLoading = true
                this.creditState = await fetchCreditState({ project: this.project, axiosPost: this.$helpers.axiosPost.bind(this.$helpers) })
                this.creditStateFailed = !this.creditState
                this.creditStateLoading = false
            },
            close() {
                if (this.loading) return
                this.$emit("input", false)
            },
            format(value) {
                return this.$helpers.formatNumber ? this.$helpers.formatNumber(value) : value
            },
            formatDate(date) {
                return date.toLocaleDateString("de-DE")
            }
        }
    }
</script>

<style scoped>
    #creditChangeConfirmDialog .qgreycard {
        font-size: 13px;
    }
    #creditChangeConfirmDialog .hint {
        font-size: 13px;
    }
</style>
